import { z } from 'zod';
/**
 * Wire header names shared by US-Shop and US-Enrollment-BFF.
 *
 * `correlationId` matches the platform's FORWARD-path convention, declared at
 * `Global%20Quotation%20Service/US/src/events/headers.ts:12`. It is NOT
 * `originalCorrelationId` — that field exists on exactly one schema in the
 * workspace (`market-ops-types/src/schemas/webhook-envelope.schema.ts:26`) and
 * is GMO's *echo* of what we sent, on the webhook RETURN path. Building the
 * forward path around `originalCorrelationId` would invent a field no service
 * reads (E-SF01 AC4, corrected 2026-07-17).
 */
export const HEADER_NAMES = {
    correlationId: 'x-correlation-id',
    idempotencyKey: 'idempotency-key',
    retryAfter: 'retry-after',
    captchaToken: 'x-captcha-token',
};
/**
 * 🚫 NEVER `z.string().uuid()`.
 *
 * Cross-service correlationIds are NOT guaranteed to be UUIDs. This is pinned
 * in the platform schema itself — `Global-Utility-Services/prisma/schema.prisma`
 * comments the `quote_status_history` correlation column: "Trace field — NOT a
 * key. Cross-service correlationIds (e.g. GMO's) are not guaranteed UUID, so
 * this is plain text, not @db.Uuid (audit 2026-06-15)". The webhook envelope
 * schema agrees: `z.string().min(1).nullable()`.
 *
 * A `.uuid()` here would reject valid live traffic (e.g. `enr-abc-123`) while
 * looking like a tightening. Match the platform: min(1), bounded, printable.
 *
 * Bounded at 128 and constrained to printable ASCII because this value is
 * echoed onto every log line and forwarded to upstreams — an unbounded or
 * control-character-bearing header is a log-injection vector, not a trace id.
 */
export const CorrelationIdSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[\x20-\x7E]+$/u, 'correlation id must be printable ASCII');
/**
 * Idempotency keys are client-chosen opaque strings (E-SF01 AC5). Same bounding
 * rationale as above: this value becomes part of a Redis key.
 */
export const IdempotencyKeySchema = z
    .string()
    .min(8)
    .max(128)
    .regex(/^[A-Za-z0-9_.:-]+$/u, 'idempotency key must be [A-Za-z0-9_.:-]');
//# sourceMappingURL=headers.js.map